import {
  Component,
  ElementRef,
  HostListener,
  inject,
  OnInit,
  OnDestroy,
  ViewChild,
} from "@angular/core";
import { CommonModule } from "@angular/common";
import { CdkTrapFocus } from "@angular/cdk/a11y";
import {
  NavigationEnd,
  Router,
  RouterLink,
  RouterOutlet,
} from "@angular/router";
import { MatButtonModule } from "@angular/material/button";
import { MatIconModule } from "@angular/material/icon";
import { MatTooltipModule } from "@angular/material/tooltip";
import { BriTeammateAuthorizationService } from "@davita/bridge-utility-library/authorization";
import { filter, Subscription } from "rxjs";
import { environment } from "../environments/environment";
import { AppSideNavComponent } from "./components/app-side-nav/app-side-nav.component";

@Component({
  selector: "app-root",
  standalone: true,
  imports: [
    CommonModule,
    CdkTrapFocus,
    RouterOutlet,
    RouterLink,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
    AppSideNavComponent,
  ],
  templateUrl: "./app.component.html",
  styleUrl: "./app.component.scss",
})
export class AppComponent implements OnInit, OnDestroy {
  isAuthEnabled = !!environment.apiBaseUrl;
  isLoggedIn = false;
  userName = "";
  isNavOpen = false;
  isMobile = false;
  demoNotice = "";
  private currentPath = "";
  private userSub?: Subscription;
  private navigationSub?: Subscription;

  @ViewChild("menuButton") private menuButton?: ElementRef<HTMLButtonElement>;

  private router = inject(Router);
  private teammateAuth = this.isAuthEnabled
    ? inject(BriTeammateAuthorizationService)
    : null;

  ngOnInit() {
    sessionStorage.removeItem("eval_jwt");
    this.updateViewport();
    this.navigationSub = this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe((event) => {
        this.closeNavigation(false);
        const path = event.urlAfterRedirects.split(/[?#]/)[0];
        if (path !== this.currentPath) {
          this.currentPath = path;
          window.scrollTo({ top: 0, left: 0 });
        }
      });

    if (this.teammateAuth) {
      this.userSub = this.teammateAuth.user$.subscribe(
        (user: { oim_details?: { userLogin?: string } } | null) => {
          this.isLoggedIn = !!user;
          this.userName = user?.oim_details?.userLogin || "";
        },
      );
    }
  }

  ngOnDestroy() {
    this.userSub?.unsubscribe();
    this.navigationSub?.unsubscribe();
  }

  @HostListener("window:resize")
  onWindowResize() {
    this.updateViewport();
  }

  @HostListener("document:keydown.escape")
  onEscape() {
    if (this.isMobile && this.isNavOpen) {
      this.closeNavigation();
    }
  }

  openNavigation() {
    if (this.isMobile) {
      this.isNavOpen = true;
    }
  }

  closeNavigation(restoreFocus = true) {
    const wasOpen = this.isNavOpen;
    this.isNavOpen = false;
    if (restoreFocus && wasOpen && this.isMobile) {
      queueMicrotask(() => this.menuButton?.nativeElement.focus());
    }
  }

  private updateViewport() {
    const wasMobile = this.isMobile;
    this.isMobile = window.matchMedia("(max-width: 960px)").matches;
    if (wasMobile && !this.isMobile) {
      this.closeNavigation(false);
    }
  }

  login() {
    this.router.navigate(["/teammate-login"]);
  }

  logout() {
    if (this.teammateAuth) {
      this.teammateAuth.logout();
    }
  }

  showDemoNotice(message: string) {
    this.demoNotice = message;
    window.setTimeout(() => {
      this.demoNotice = "";
    }, 3200);
  }
}
